interface Point {
  x: number;
  y: number;
}

interface Segment {
  start: Point;
  end: Point;
}

interface RayHit {
  point: Point;
  distance: number;
}

/**
 * Find the intersection of a ray (origin + direction) with a finite line segment.
 * Returns the intersection point and distance along the ray, or null if no hit.
 *
 * Uses the standard 2D parametric ray-line formula:
 *   ray: P = origin + t * direction  (t >= 0)
 *   seg: Q = start + u * (end - start)  (0 <= u <= 1)
 */
export const raySegmentIntersection = (
  origin: Point,
  direction: Point,
  segment: Segment
): RayHit | null => {
  const dx = segment.end.x - segment.start.x;
  const dy = segment.end.y - segment.start.y;

  const denom = direction.x * dy - direction.y * dx;

  // Parallel — no intersection
  if (Math.abs(denom) < 1e-10) return null;

  const ox = segment.start.x - origin.x;
  const oy = segment.start.y - origin.y;

  // t = distance along the ray
  const t = (ox * dy - oy * dx) / denom;
  if (t < 0) return null; // Intersection is behind the ray origin

  // u = position along the segment (0..1)
  const u = (ox * direction.y - oy * direction.x) / denom;
  if (u < 0 || u > 1) return null; // Outside the segment

  return {
    point: {
      x: origin.x + t * direction.x,
      y: origin.y + t * direction.y
    },
    distance: t
  };
};
